// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  Flex,
  List,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import range from 'lodash-es/range';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';

import { useIpadMediaQuery, useKioskSpacing } from '../../hooks';

interface QuantityButtonProps {
  existingProductQty: number | undefined;
  maxProductQty: number;
  onAddToBagClick: (quantity: number) => void;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
}

const QtyPopover = styled(Popover)(() => {
  return {
    maxHeight: 396,
  };
});

const QtySelector = styled(Button)(({ theme }) => {
  const isIpad = useIpadMediaQuery();

  return {
    borderColor: theme.colors.primary.dark,
    backgroundColor: theme.colors.primary.dark,
    borderRadius: '16px 0px 0px 16px',
    paddingLeft: isIpad ? 16 : 24,
    paddingRight: isIpad ? 12 : 16,
  };
});

const AddToBagBtn = styled(Button)(({ disabled }) => {
  const isIpad = useIpadMediaQuery();

  return {
    borderRadius: '0px 16px 16px 0px',
    paddingLeft: isIpad ? 16 : 24,
    paddingRight: isIpad ? 16 : 24,
    right: disabled ? 0 : 1,
    zIndex: 1,
  };
});

export const QuantityButton = ({
  onAddToBagClick,
  setQuantity,
  existingProductQty,
  maxProductQty,
  quantity,
}: QuantityButtonProps) => {
  const { isIpad, defaultSpacing } = useKioskSpacing();

  const quantityOptions = useMemo(
    () =>
      range(1, maxProductQty - (existingProductQty ?? 0) + 1).map((q) => ({
        label: q.toString(),
        value: q,
      })),
    [existingProductQty, maxProductQty]
  );

  return (
    <Flex>
      <QtyPopover
        alignment={{ horizontal: 'right', vertical: 'bottom' }}
        disableMobileStyling
        label="Select quantity"
        ml={defaultSpacing}
        openOn={quantityOptions.length === 0 ? 'disabled' : 'click'}
        target={
          <QtySelector
            label={
              <Typography color="text-info" variant="body-bold">
                {quantity}
              </Typography>
            }
            size={isIpad ? 'small' : 'default'}
            endIcon={<ChevronDownIcon size="sm" color="inherit" />}
            disabled={quantityOptions.length === 0}
          />
        }
      >
        {({ closePopover }: PopoverContextProps) => (
          <Popover.Content padding={false}>
            <List label="quantity selector" py={16}>
              {quantityOptions.map((qty) => (
                <List.Item
                  ariaLabel={qty.label}
                  onClick={() => {
                    setQuantity(qty.value);
                    closePopover();
                  }}
                  key={qty.label}
                  px={24}
                  py={12}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                  >
                    <Typography variant="body">{qty.value}</Typography>
                    {quantity === qty.value && <CheckIcon />}
                  </Flex>
                </List.Item>
              ))}
            </List>
          </Popover.Content>
        )}
      </QtyPopover>
      <AddToBagBtn
        label={
          <Typography color="text-info" variant="body-bold">
            Add to bag
          </Typography>
        }
        onClick={() => {
          onAddToBagClick(quantity);
        }}
        size={isIpad ? 'small' : 'default'}
        disabled={quantityOptions.length === 0}
      />
    </Flex>
  );
};
